import React from 'react';
import { NormalizedButton } from 'lib/button';
import { ElementStateWrapper } from 'lib/core';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { STEPPER_POSITIONS, STEPPER_VARIANTS } from '../constants';

import { stepStyles } from '../stepperStyles';

const StyledNormalizedButton = styled(NormalizedButton)`
  ${({ theme }) => css`
    align-items: center;
    background-color: transparent;
    border-radius: ${theme.size.borderRadius.large.value};
    display: flex;
    flex-direction: ${({ position }) => (position === STEPPER_POSITIONS.BOTTOM ? 'column' : 'row')};
    padding: ${theme.size.spacing.medium.value};
    width: 100%;
    text-align: left;
    ${({ position }) =>
      position === STEPPER_POSITIONS.BOTTOM &&
      css`
        text-align: center;
      `};
  `}
`;

export const Step = ({
  children = undefined,
  isActive = false,
  isComplete = false,
  isSimple = false,
  onClick = () => {},
  position = STEPPER_POSITIONS.RIGHT,
  variant,
  ...other
}) => {
  const handleStepClick = () => {
    if (isActive || isComplete) {
      onClick();
    }
  };
  const attributes = isActive ? { 'aria-current': 'step' } : {};

  const childrenWithProps = React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { isActive, isComplete, position }) : child
  );

  const getStateStyles = () => {
    if (variant === 'simple') {
      return stepStyles.simple;
    } else return isActive || isComplete ? stepStyles.selected : stepStyles.unselected;
  };

  const stateStyles = getStateStyles();

  return (
    <ElementStateWrapper stateStyles={stateStyles} useFocusStyles={true} {...other}>
      <StyledNormalizedButton
        disabled={!(isActive || isComplete)}
        onClick={handleStepClick}
        {...{ isActive, isComplete, isSimple, position }}
        {...attributes}
        {...other}
      >
        {childrenWithProps}
      </StyledNormalizedButton>
    </ElementStateWrapper>
  );
};

Step.propTypes = {
  /** Any content inserted between component tags */
  children: PropTypes.node,
  /** Position of the labels. Is passed to child components. */
  position: PropTypes.string,
  /** Callback that is called on click */
  onClick: PropTypes.func,
  /** Sets the step as active. Is passed to child components. */
  isActive: PropTypes.bool,
  /** Mark the step as completed. Is passed to child components. */
  isComplete: PropTypes.bool,
  /** Denotes a simple stepper (one at a time). Is passed to child components. */
  isSimple: PropTypes.bool,
  /** Variant of the stepper */
  variant: PropTypes.oneOf(Object.values(STEPPER_VARIANTS)),
};
